import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";
import { AdminDetailPanel } from "@/components/admin/AdminDetailPanel";
import { AdminDrilldownWorkspace } from "@/components/admin/AdminDrilldownWorkspace";
import {
  Target, CheckCircle, XCircle, Clock, User, MapPin, Phone, Mail,
  MessageSquare, Loader2, TrendingUp, BarChart3, Users, Award, ChevronRight, Star
} from "lucide-react";

const STATUS_COLORS = { pending: "bg-amber-100 text-amber-800", accepted: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };

export default function AdminCoordinators() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});
  const [kpis, setKpis] = useState(null);
  const [activeCoords, setActiveCoords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState(null);
  const [perfDetail, setPerfDetail] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [drilldown, setDrilldown] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [appRes, kpiRes] = await Promise.all([
        api.get("/coordinator/applications"),
        api.get("/recruitment/kpis"),
      ]);
      const applicationRows = appRes.data.applications || [];
      setApps(applicationRows);
      setCounts(appRes.data.counts || {});
      setKpis(kpiRes.data);
      const acceptedApps = applicationRows.filter((app) => app.status === "accepted");
      const kpiCoordinators = kpiRes.data.coordinators || [];
      const merged = acceptedApps.map((app) => {
        const perf = kpiCoordinators.find((coord) => coord.id === app.user_id || coord.email === app.email) || {};
        return {
          id: app.user_id,
          name: app.name,
          email: app.email,
          primary_city: app.city,
          city: app.city,
          total: perf.total || 0,
          live: perf.live || 0,
          verbal: perf.verbal || 0,
          conversion: perf.conversion || 0,
          application: app,
        };
      });
      setActiveCoords(merged);
    } catch {}
    setLoading(false);
  };

  const handleReview = async (appId, action) => {
    try {
      const res = await api.post(`/coordinator/applications/${appId}/review`, { action, reason: rejectReason });
      if (action === "accept" && res.data.temp_password) {
        toast.success(`Accepted! Temp password: ${res.data.temp_password}`, { duration: 15000 });
        try { navigator.clipboard.writeText(res.data.temp_password); } catch {}
      } else { toast.success(`Application ${action}ed`); }
      setDetail(null); setRejectReason(""); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openPerformance = async (coordId) => {
    try {
      // Get coordinator prospects
      const res = await api.get(`/recruitment/prospects?coordinator=${coordId}`);
      const prospects = res.data.prospects || [];
      const live = prospects.filter(p => p.current_stage === "live_published").length;
      const verbal = prospects.filter(p => p.current_stage === "verbal_commitment").length;
      const onboarding = prospects.filter(p => p.current_stage === "onboarding_in_progress").length;
      const stalled = prospects.filter(p => !["live_published", "stalled_lost"].includes(p.current_stage)).length;
      const cancelled = prospects.filter(p => p.current_stage === "stalled_lost").length;
      const conversion = prospects.length > 0 ? Math.round(live / prospects.length * 100) : 0;

      // Stage breakdown
      const stages = {};
      prospects.forEach(p => { stages[p.current_stage] = (stages[p.current_stage] || 0) + 1; });

      const coord = activeCoords.find(c => c.id === coordId) || {};
      const app = apps.find(a => a.user_id === coordId || a.email === coord.email) || coord.application || {};
      setPerfDetail({
        ...coord, ...app, prospects, live, verbal, onboarding, stalled, cancelled,
        conversion, stages, total: prospects.length,
      });
    } catch { toast.error("Failed to load performance data"); }
  };

  const supply = kpis?.supply || {};
  const pipeline = kpis?.pipeline || {};
  const cityOptions = Array.from(new Set(apps.map((app) => app.city).filter(Boolean))).sort();
  const filteredApps = useMemo(() => {
    return apps.filter((app) => {
      const matchesSearch = !searchQuery || [app.name, app.email, app.city, app.province].filter(Boolean).join(" ").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || app.status === statusFilter;
      const matchesCity = cityFilter === "all" || app.city === cityFilter;
      return matchesSearch && matchesStatus && matchesCity;
    });
  }, [apps, searchQuery, statusFilter, cityFilter]);
  const filteredPendingApps = filteredApps.filter((app) => app.status === "pending");
  const filteredCoordinators = useMemo(() => {
    return activeCoords.filter((coord) => {
      const matchesSearch = !searchQuery || [coord.name, coord.id].filter(Boolean).join(" ").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCity = cityFilter === "all" || (coord.primary_city || coord.city) === cityFilter;
      return matchesSearch && matchesCity;
    });
  }, [activeCoords, searchQuery, cityFilter]);
  const activeFilterChips = [
    searchQuery ? { key: "search", label: `Search: ${searchQuery}`, onRemove: () => setSearchQuery("") } : null,
    statusFilter !== "all" ? { key: "status", label: `Status: ${statusFilter}`, onRemove: () => setStatusFilter("all") } : null,
    cityFilter !== "all" ? { key: "city", label: `City: ${cityFilter}`, onRemove: () => setCityFilter("all") } : null,
  ].filter(Boolean);

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const loadKpiDrilldown = async (type) => {
    try {
      const prospectsRes = await api.get('/recruitment/prospects');
      const prospects = prospectsRes.data.prospects || [];
      const acceptedApps = apps.filter(app => app.status === 'accepted');
      if (type === 'active') setDrilldown({ title: 'Active Coordinators', items: acceptedApps.map(app => ({ id: app.user_id || app.application_id, title: app.name, subtitle: `${app.email} · ${app.city || 'No city'}` })) });
      if (type === 'prospects') setDrilldown({ title: 'Total Prospects', items: prospects.map(p => ({ id: p.prospect_id, title: p.venue_name, subtitle: `${p.municipality || 'No city'} · ${p.current_stage}` })) });
      if (type === 'live') setDrilldown({ title: 'Venues Live', items: prospects.filter(p => p.current_stage === 'live_published').map(p => ({ id: p.prospect_id, title: p.venue_name, subtitle: `${p.municipality || 'No city'} · LIVE` })) });
      if (type === 'onboarding') setDrilldown({ title: 'Venues In Onboarding', items: prospects.filter(p => p.current_stage === 'onboarding_in_progress').map(p => ({ id: p.prospect_id, title: p.venue_name, subtitle: `${p.municipality || 'No city'} · onboarding` })) });
      if (type === 'stalled') setDrilldown({ title: 'Stalled Deals', items: prospects.filter(p => p.current_stage === 'stalled_lost').map(p => ({ id: p.prospect_id, title: p.venue_name, subtitle: `${p.municipality || 'No city'} · stalled/lost` })) });
    } catch {
      toast.error('Failed to load KPI drilldown');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-coordinators-hub">
      <div className="flex items-center gap-3">
        <Target className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl">Field Coordinators</h1>
        <div className="flex gap-1 ml-auto">
          {Object.entries(counts).map(([k, v]) => <Badge key={k} className={STATUS_COLORS[k] || "bg-muted"}>{k}: {v}</Badge>)}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="overview">KPIs & Stats</TabsTrigger>
          <TabsTrigger value="active">Active ({activeCoords.length})</TabsTrigger>
          <TabsTrigger value="applicants">Applicants ({counts.pending || 0})</TabsTrigger>
          <TabsTrigger value="all">All Applications</TabsTrigger>
        </TabsList>

        {/* ── KPIs & Stats ── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="cursor-pointer" onClick={() => loadKpiDrilldown('active')}><CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{activeCoords.length}</p>
              <p className="text-xs text-muted-foreground">Active Coordinators</p>
            </CardContent></Card>
            <Card className="cursor-pointer" onClick={() => loadKpiDrilldown('prospects')}><CardContent className="p-4 text-center">
              <Target className="w-5 h-5 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{supply.total_prospects || 0}</p>
              <p className="text-xs text-muted-foreground">Total Prospects</p>
            </CardContent></Card>
            <Card className="cursor-pointer" onClick={() => loadKpiDrilldown('live')}><CardContent className="p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{supply.venues_live || 0}</p>
              <p className="text-xs text-muted-foreground">Venues Live</p>
            </CardContent></Card>
            <Card className="cursor-pointer" onClick={() => loadKpiDrilldown('prospects')}><CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{supply.conversion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Conversion Rate</p>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Card className="cursor-pointer" onClick={() => loadKpiDrilldown('onboarding')}><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{supply.venues_onboarding || 0}</p>
              <p className="text-[10px] text-muted-foreground">In Onboarding</p>
            </CardContent></Card>
            <Card className="cursor-pointer" onClick={() => loadKpiDrilldown('stalled')}><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{pipeline.stalled_deals || 0}</p>
              <p className="text-[10px] text-muted-foreground">Stalled Deals</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{pipeline.weekly_meetings || 0}</p>
              <p className="text-[10px] text-muted-foreground">Weekly Meetings</p>
            </CardContent></Card>
          </div>

          {/* Cluster density */}
          {(kpis?.clusters || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Cluster Density</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {kpis.clusters.slice(0, 8).map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded border">
                      <span className="text-sm">{c.municipality}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-bold">{c.venues_live}</span>
                        {c.event_ready && <Badge className="bg-green-100 text-green-800 text-[8px]">Ready</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {drilldown && (
            <AdminDrilldownWorkspace
              title={drilldown.title}
              items={drilldown.items}
              emptyText="No records found for this KPI."
              onSelect={(item) => item.id?.startsWith('user_') ? openPerformance(item.id) : null}
              renderDetail={(item) => (
                <div className="space-y-3 text-sm">
                  <p className="font-medium text-zinc-900">{item.title}</p>
                  <p className="text-zinc-500">{item.subtitle}</p>
                  <p className="text-xs text-zinc-500">Use the relevant list row or coordinator detail to continue exploring this KPI bucket.</p>
                </div>
              )}
              testId="coordinator-kpi-drilldown"
            />
          )}
        </TabsContent>

        {/* ── Active Coordinators ── */}
        <TabsContent value="active" className="space-y-3 mt-4">
          <AdminFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search coordinators by name or ID"
            filters={[
              { key: "city", label: "City", value: cityFilter, onChange: setCityFilter, options: [{ value: "all", label: "All Cities" }, ...cityOptions.map((city) => ({ value: city, label: city }))], testId: "coordinator-city-filter" },
            ]}
            activeFilters={activeFilterChips}
            onClearAll={() => { setSearchQuery(""); setCityFilter("all"); setStatusFilter("all"); }}
            searchTestId="coordinator-search-input"
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-6 border border-zinc-200 bg-white">
            <div className="lg:col-span-8 border-r border-zinc-200">
              {filteredCoordinators.length === 0 ? <div className="p-8 text-center text-zinc-500">No coordinators match the current filters.</div> : filteredCoordinators.map((c, i) => (
                <button key={i} type="button" className="w-full px-6 py-4 border-b border-zinc-200 hover:bg-zinc-50 text-left transition-colors" onClick={() => openPerformance(c.id)} data-testid={`coordinator-row-${c.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-zinc-900">{c.name}</p>
                      <p className="text-xs text-zinc-500">{c.total} prospects · {c.live} live · {c.verbal} verbal</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-heading text-lg font-bold text-blue-600">{c.conversion}%</p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">conversion</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="lg:col-span-4">
              <AdminDetailPanel title={perfDetail?.name} subtitle={perfDetail ? `${perfDetail.total} prospects · ${perfDetail.live} live` : "Select a coordinator to inspect performance"} testId="coordinator-detail-panel">
                {perfDetail ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <StatPill label="Live" value={perfDetail.live} />
                      <StatPill label="Verbal" value={perfDetail.verbal} />
                      <StatPill label="Conv." value={`${perfDetail.conversion}%`} />
                    </div>
                    <div className="space-y-2">
                      {Object.entries(perfDetail.stages || {}).map(([stage, count]) => (
                        <div key={stage} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2">
                          <span className="capitalize text-zinc-600">{stage.replace(/_/g, " ")}</span>
                          <span className="font-medium text-zinc-900">{count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm space-y-2">
                      <p><span className="text-zinc-500">Email:</span> {perfDetail.email || '—'}</p>
                      <p><span className="text-zinc-500">Phone:</span> {perfDetail.phone || '—'}</p>
                      <p><span className="text-zinc-500">Address:</span> {perfDetail.address || '—'}</p>
                      <p><span className="text-zinc-500">Facebook:</span> {perfDetail.facebook || '—'}</p>
                      <p><span className="text-zinc-500">Education:</span> {[perfDetail.edu_level, perfDetail.edu_school, perfDetail.edu_year].filter(Boolean).join(' · ') || '—'}</p>
                      <p><span className="text-zinc-500">How they knew SetPlay:</span> {perfDetail.q_how_know || '—'}</p>
                      <p><span className="text-zinc-500">Why interested:</span> {perfDetail.q_why_interested || '—'}</p>
                      <p><span className="text-zinc-500">Contribution:</span> {perfDetail.q_contribution || '—'}</p>
                    </div>
                  </div>
                ) : null}
              </AdminDetailPanel>
            </div>
          </div>
        </TabsContent>

        {/* ── Pending Applicants ── */}
        <TabsContent value="applicants" className="space-y-3 mt-4">
          <AdminFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search applicants by name, email, city"
            filters={[
              { key: "city", label: "City", value: cityFilter, onChange: setCityFilter, options: [{ value: "all", label: "All Cities" }, ...cityOptions.map((city) => ({ value: city, label: city }))], testId: "applicant-city-filter" },
            ]}
            activeFilters={activeFilterChips.filter((chip) => chip.key !== 'status')}
            onClearAll={() => { setSearchQuery(""); setCityFilter("all"); setStatusFilter("all"); }}
            searchTestId="applicant-search-input"
          />
          <ApplicationWorkspace applications={filteredPendingApps} detail={detail} setDetail={setDetail} rejectReason={rejectReason} setRejectReason={setRejectReason} handleReview={handleReview} title="Pending Applicants" emptyText="No pending applications match the current filters." />
        </TabsContent>

        {/* ── All Applications ── */}
        <TabsContent value="all" className="space-y-3 mt-4">
          <AdminFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search all coordinator applications"
            filters={[
              { key: "status", label: "Status", value: statusFilter, onChange: setStatusFilter, options: [{ value: "all", label: "All Statuses" }, { value: "pending", label: "Pending" }, { value: "accepted", label: "Accepted" }, { value: "rejected", label: "Rejected" }], testId: "application-status-filter" },
              { key: "city", label: "City", value: cityFilter, onChange: setCityFilter, options: [{ value: "all", label: "All Cities" }, ...cityOptions.map((city) => ({ value: city, label: city }))], testId: "application-city-filter" },
            ]}
            activeFilters={activeFilterChips}
            onClearAll={() => { setSearchQuery(""); setCityFilter("all"); setStatusFilter("all"); }}
            searchTestId="all-application-search-input"
          />
          <ApplicationWorkspace applications={filteredApps} detail={detail} setDetail={setDetail} rejectReason={rejectReason} setRejectReason={setRejectReason} handleReview={handleReview} title="All Applications" emptyText="No applications match the current filters." />
        </TabsContent>
      </Tabs>

      {/* ── Applicant Detail Dialog ── */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {detail.name} <Badge className={STATUS_COLORS[detail.status]}>{detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{detail.email}</div>
                  <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{detail.phone || "—"}</div>
                  <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{detail.city}, {detail.province}</div>
                  {detail.facebook && <div>Facebook: {detail.facebook}</div>}
                  {detail.address && <div className="col-span-2">Address: {detail.address}</div>}
                </div>
                {detail.edu_level && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-700 mb-1">Education</p>
                    <p className="text-sm">{detail.edu_level}{detail.edu_school ? ` — ${detail.edu_school}` : ""}{detail.edu_year ? ` (${detail.edu_year})` : ""}</p>
                  </div>
                )}
                {detail.experience && <div><p className="text-xs font-medium text-muted-foreground">Experience</p><p className="text-sm mt-1">{detail.experience}</p></div>}
                <div className="space-y-3 bg-slate-50 rounded-lg p-4">
                  <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Application Answers</p>
                  <div><p className="text-xs font-medium text-muted-foreground mb-1">How did you hear about SetPlay?</p><p className="text-sm bg-white rounded p-2 border">{detail.q_how_know || "—"}</p></div>
                  <div><p className="text-xs font-medium text-muted-foreground mb-1">Why are you interested in joining?</p><p className="text-sm bg-white rounded p-2 border">{detail.q_why_interested || "—"}</p></div>
                  <div><p className="text-xs font-medium text-muted-foreground mb-1">What can you contribute?</p><p className="text-sm bg-white rounded p-2 border">{detail.q_contribution || "—"}</p></div>
                </div>
                {detail.status === "pending" && (
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => handleReview(detail.application_id, "accept")} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full">
                      <CheckCircle className="w-4 h-4 mr-1" />Accept
                    </Button>
                    <div className="flex-1 space-y-2">
                      <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Rejection reason (optional)" className="text-sm" />
                      <Button onClick={() => handleReview(detail.application_id, "reject")} variant="outline" className="w-full rounded-full border-red-200 text-red-600">
                        <XCircle className="w-4 h-4 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Performance Analytics Dialog ── */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {perfDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />{perfDetail.name} — Performance
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* KPI Cards */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  <Card><CardContent className="p-2 text-center"><p className="text-lg font-bold">{perfDetail.total}</p><p className="text-[9px] text-muted-foreground">Total</p></CardContent></Card>
                  <Card><CardContent className="p-2 text-center"><p className="text-lg font-bold text-green-600">{perfDetail.live}</p><p className="text-[9px] text-muted-foreground">Live</p></CardContent></Card>
                  <Card><CardContent className="p-2 text-center"><p className="text-lg font-bold text-amber-600">{perfDetail.verbal}</p><p className="text-[9px] text-muted-foreground">Verbal</p></CardContent></Card>
                  <Card><CardContent className="p-2 text-center"><p className="text-lg font-bold text-blue-600">{perfDetail.onboarding}</p><p className="text-[9px] text-muted-foreground">Onboarding</p></CardContent></Card>
                  <Card><CardContent className="p-2 text-center"><p className="text-lg font-bold text-orange-500">{perfDetail.stalled}</p><p className="text-[9px] text-muted-foreground">Active</p></CardContent></Card>
                  <Card><CardContent className="p-2 text-center"><p className="text-lg font-bold text-primary">{perfDetail.conversion}%</p><p className="text-[9px] text-muted-foreground">Conv.</p></CardContent></Card>
                </div>

                {/* Stage Breakdown */}
                <Card>
                  <CardHeader><CardTitle className="text-sm">Pipeline Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {Object.entries(perfDetail.stages || {}).map(([stage, count]) => (
                        <div key={stage} className="flex items-center justify-between py-1">
                          <span className="text-xs capitalize">{stage.replace(/_/g, " ")}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (count / Math.max(perfDetail.total, 1)) * 100)}%` }} />
                            </div>
                            <span className="text-xs font-bold w-6 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Prospects */}
                <Card>
                  <CardHeader><CardTitle className="text-sm">Prospects ({perfDetail.prospects?.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {(perfDetail.prospects || []).slice(0, 15).map(p => (
                        <div key={p.prospect_id} className="flex items-center justify-between py-1 border-b last:border-0">
                          <div>
                            <span className="text-xs font-medium">{p.venue_name}</span>
                            <span className="text-[10px] text-muted-foreground ml-2">{p.municipality}</span>
                          </div>
                          <Badge variant="outline" className="text-[8px]">{p.current_stage?.replace(/_/g, " ")}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-center">
      <p className="font-heading text-lg font-bold text-zinc-900">{value}</p>
      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</p>
    </div>
  );
}

function ApplicationWorkspace({ applications, detail, setDetail, rejectReason, setRejectReason, handleReview, title, emptyText }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-6 border border-zinc-200 bg-white">
      <div className="lg:col-span-8 border-r border-zinc-200">
        {applications.length === 0 ? <div className="p-8 text-center text-zinc-500">{emptyText}</div> : applications.map((app) => (
          <button key={app.application_id} type="button" className="w-full px-6 py-4 border-b border-zinc-200 hover:bg-zinc-50 text-left transition-colors" onClick={() => setDetail(app)} data-testid={`application-row-${app.application_id}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-zinc-900">{app.name}</p>
                  <Badge className={STATUS_COLORS[app.status] || "bg-zinc-100 text-zinc-700"}>{app.status}</Badge>
                </div>
                <p className="text-xs text-zinc-500 mt-1">{app.email} · {app.city || "No city"}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-zinc-500">{app.created_at?.slice(0, 10)}</p>
                {app.nda_signed && <Badge className="mt-1 bg-green-100 text-green-800 text-[9px]">NDA</Badge>}
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="lg:col-span-4">
        <AdminDetailPanel title={detail?.name} subtitle={detail ? `${detail.email} · ${detail.city || detail.province || 'No city'}` : title} testId="coordinator-application-detail-panel">
          {detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-2 text-sm text-zinc-700">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-zinc-400" />{detail.email}</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-400" />{detail.phone || '—'}</div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-400" />{detail.city || '—'}, {detail.province || '—'}</div>
                  <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-zinc-400" />{detail.facebook || 'No Facebook provided'}</div>
                  <div className="col-span-full"><span className="text-zinc-500">Address:</span> {detail.address || '—'}</div>
                  <div className="col-span-full"><span className="text-zinc-500">Education:</span> {[detail.edu_level, detail.edu_school, detail.edu_year].filter(Boolean).join(' · ') || '—'}</div>
              </div>
              <div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Experience</p><p className="text-sm">{detail.experience || '—'}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">How they knew SetPlay</p><p className="text-sm">{detail.q_how_know || '—'}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Why Join</p><p className="text-sm">{detail.q_why_interested || '—'}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Contribution</p><p className="text-sm">{detail.q_contribution || '—'}</p></div>
              </div>
              {detail.status === "pending" && (
                <div className="space-y-3">
                  <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Optional rejection reason" data-testid="coordinator-reject-reason" />
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => handleReview(detail.application_id, "accept")} className="bg-green-600 hover:bg-green-700 text-white rounded-md" data-testid="coordinator-accept-btn">
                      <CheckCircle className="w-4 h-4 mr-2" />Accept
                    </Button>
                    <Button variant="outline" onClick={() => handleReview(detail.application_id, "reject")} className="border-red-200 text-red-600 rounded-md" data-testid="coordinator-reject-btn">
                      <XCircle className="w-4 h-4 mr-2" />Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </AdminDetailPanel>
      </div>
    </div>
  );
}
